import React, { useState, useEffect, useCallback, useRef } from 'react'
import { connect } from "react-redux";
import { makeStyles } from '@material-ui/core/styles';
import {
	Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip, Tooltip,
	IconButton, Divider, LinearProgress, CircularProgress
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import CloseIcon from '@material-ui/icons/Close';
import PublishIcon from '@material-ui/icons/Publish';
import BlockIcon from '@material-ui/icons/Block';
import ReplayIcon from '@material-ui/icons/Replay';
import { DownloadOutlined } from '@ant-design/icons';
import { CSVLink } from "react-csv";
import XLSX from 'xlsx';
import BomTable from '../../components/bomTable';
import AlertDialog from '../bom/dialogBoxPopUp';
import HttpService from '../../services/httpService';
import HardwareBomService from '../../services/hardwareBom.service';
import { setSnackInfo } from '../../redux/actions/appAction';

const NAVY = "#1C375D";
const CREAM = "#FFF2EB";

// Same sheet/column contract the Upload New BOM screen uses.
const SAMPLE_HEADERS = [
	{ key: 'selectedRoom', label: 'Select Room' },
	{ key: 'selectedScope', label: 'Select Scope' },
	{ key: 'selectedSubCategory', label: 'Scope Sub category' },
	{ key: 'selectedMaterial', label: 'Select Material' },
	{ key: 'uom', label: 'UOM' },
	{ key: 'quantity', label: 'Qty' }
];
const SAMPLE_DATA = [
	{
		selectedRoom: 'Sample Room',
		selectedScope: 'Sample Scope',
		selectedSubCategory: 'Sample SubCategory',
		selectedMaterial: 'Sample Material',
		uom: 'Sample Unit',
		quantity: "Sample Quantity"
	}
];

const MODULAR_FLOW_BOM_TYPES = ["Modular BOM", "Hardware BOM", "Non-Hettich BOM"];
const BOM_SHEET_INDEX = 2;
const LOADER_TIMEOUT_MS = 12000;
// States that mean the project already has a stored BOM worth waiting for.
const STORED_BOM_STATES = ["approved", "pending", "rejected", "draft"];
// If the enter transition never reports back, mount the table anyway.
const CONTENT_MOUNT_FALLBACK_MS = 400;

// The material list is large and identical for every upload, so fetch it at most once a minute.
const MATERIAL_NAMES_TTL_MS = 60 * 1000;
let materialNamesCache = { expiresAt: 0, promise: null };
const loadMaterialNames = () => {
	if (materialNamesCache.promise && materialNamesCache.expiresAt > Date.now()) return materialNamesCache.promise;
	const promise = HttpService.getAllMaterialsNames().then(result => new Set(result.data.map(material => material.name)));
	promise.catch(() => { materialNamesCache = { expiresAt: 0, promise: null } });
	materialNamesCache = { promise, expiresAt: Date.now() + MATERIAL_NAMES_TTL_MS };
	return promise;
};

const STATE_STYLES = {
	approved: { background: "#DDF0DE", color: "#2E7D32", border: "1px solid #BFE0C1" },
	pending: { background: "#FDF3DC", color: "#B98900", border: "1px solid #F0DFAF" },
	rejected: { background: "#FBE0E0", color: "#D64545", border: "1px solid #F2C4C4" },
	draft: { background: "#E9EEF6", color: "#42557A", border: "1px solid #D3DDEC" },
	skipped: { background: "#ECEFF3", color: "#66748C", border: "1px solid #D8DEE8" },
	notUploaded: { background: "#FBE0E0", color: "#D64545", border: "1px solid #F2C4C4" }
};

const useStyles = makeStyles((theme) => ({
	title: {
		background: `linear-gradient(120deg, ${NAVY} 0%, #2A4E80 100%)`,
		color: CREAM,
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between"
	},
	titleMain: { fontSize: 18, fontWeight: 700, margin: 0 },
	titleSub: { fontSize: 13, marginTop: 4, color: "rgba(255,242,235,0.8)" },
	closeButton: { color: CREAM },
	progressSlot: { height: 4 },
	progressBar: { height: 4, backgroundColor: "#E1E9F4", "& .MuiLinearProgress-bar": { backgroundColor: "#F47E46" } },
	content: { paddingTop: theme.spacing(2), backgroundColor: "#FBFCFE" },
	sectionLabel: { fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: theme.spacing(1) },
	actionRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: theme.spacing(2) },
	uploadButton: { background: NAVY, color: "#FFF", margin: 0, textTransform: "none", fontWeight: 600 },
	skipButton: { textTransform: "none", fontWeight: 600, borderColor: "#C4CDDB", color: "#5A6A85" },
	skipButtonActive: { textTransform: "none", fontWeight: 600, background: "#5A6A85", color: "#FFF" },
	hiddenInput: { display: "none" },

	// The table stays mounted underneath so the swap in and out of loading never jumps.
	tableArea: { position: "relative", minHeight: 220 },
	tableBusy: { opacity: 0.35, pointerEvents: "none", filter: "blur(1px)", transition: "opacity 160ms ease" },
	overlay: {
		position: "absolute",
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		zIndex: 3,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "flex-start",
		paddingTop: theme.spacing(5),
		backgroundColor: "rgba(251,252,254,0.72)",
		borderRadius: 12
	},
	overlayMessage: { marginTop: theme.spacing(1.5), fontSize: 14, fontWeight: 600, color: NAVY },
	overlayHint: { marginTop: 4, fontSize: 12, color: "#7A879B" },
	skeletonRow: { display: "flex", gap: 10, marginBottom: 10 },

	skippedPanel: {
		border: "1px dashed #C4CDDB",
		borderRadius: 12,
		padding: theme.spacing(4),
		textAlign: "center",
		color: "#66748C",
		backgroundColor: "#F6F8FB"
	},
	skippedTitle: { fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 6 },
	statusChip: { fontWeight: 700, height: 24 }
}));

const BomUploadDialog = (props) => {
	const classes = useStyles();
	const { open, project, bomType, status, onClose, setSnackInfo } = props

	const [skipped, setSkipped] = useState(false)
	const [savingSkip, setSavingSkip] = useState(false)
	const [refreshKey, setRefreshKey] = useState(0)
	const [tableReady, setTableReady] = useState(false)
	// Non-null whenever the dialog is working: doubles as the message shown on the overlay.
	const [busyMessage, setBusyMessage] = useState(null)
	const [missingMaterialMessage, setMissingMaterialMessage] = useState("")
	const [missingMaterialOpen, setMissingMaterialOpen] = useState(false)
	// The table is the expensive half of this dialog, so it is mounted only once the shell
	// is on screen - that is what makes the click feel instant.
	const [contentMounted, setContentMounted] = useState(false)
	const [hardwareBomFiles, setHardwareBomFiles] = useState(null)
	const closedRef = useRef(false)

	useEffect(() => {
		if (!open) return
		closedRef.current = false
		setSkipped(Boolean(status?.skipped))
		setTableReady(false)
		setBusyMessage(null)
		setHardwareBomFiles(null)
	}, [open, status])

	// Opening: shell paints first, table mounts after the enter transition settles.
	useEffect(() => {
		if (!open) return undefined
		const timer = setTimeout(() => setContentMounted(true), CONTENT_MOUNT_FALLBACK_MS)
		return () => clearTimeout(timer)
	}, [open])

	// Closing: let the fade start on its own frame, then drop the table on the next one, so
	// the click never pays for unmounting a few hundred autocompletes.
	useEffect(() => {
		if (open) return undefined
		const frame = requestAnimationFrame(() => setContentMounted(false))
		return () => cancelAnimationFrame(frame)
	}, [open])

	// Warm the material list while the user is still looking at the table.
	useEffect(() => {
		if (open) loadMaterialNames().catch(() => { })
	}, [open])

	// A load that never reports back must not leave the user staring at a spinner.
	useEffect(() => {
		const waiting = Boolean(busyMessage) || (!skipped && !tableReady)
		if (!open || !waiting) return undefined
		// Nothing may ever call back - never leave the user stuck behind a spinner, and say so
		// rather than pretending the table finished loading.
		const timer = setTimeout(() => {
			setTableReady(true)
			setBusyMessage(null)
			setSnackInfo("The BOM is taking longer than usual to load. You can still upload a new one.", "warning")
		}, LOADER_TIMEOUT_MS)
		return () => clearTimeout(timer)
		// eslint-disable-next-line
	}, [open, busyMessage, skipped, tableReady])

	// BomTable expects the shape the Upload New BOM autocomplete hands it.
	const selectedProject = project
		? {
			_id: project.projectId,
			code: project.projectNumber,
			clientName: project.clientName,
			searchName: `${project.projectNumber}_${project.clientName || ""}`
		}
		: {}

	// The column key is a screen label: the Modular split gives two columns the same stored
	// bomType, so uploads and reads always go through storedBomType, never the key.
	const storedBomType = bomType?.storedBomType || bomType?.key
	const isMismatchedHardware = Boolean(bomType?.isMismatchedHardware)
	const generatesHardwareBom = storedBomType === "Hardware BOM" && !isMismatchedHardware

	const uploadEndpoint = useCallback((projectId, payload) => {
		if (storedBomType === "Initiation BOM-Carpentry BOM") return HttpService.uploadBomData(projectId, payload)
		if (MODULAR_FLOW_BOM_TYPES.includes(storedBomType)) return HttpService.uploadModularBomData(projectId, payload)
		if (storedBomType === "Snag BOM") return HttpService.uploadSnagBomData(projectId, payload)
		return null
	}, [storedBomType])

	const onTableReady = useCallback(() => {
		if (closedRef.current) return
		setTableReady(true)
		setBusyMessage(null)
	}, [])

	// Reads the BOM sheet straight into row objects - no CSV round trip, no quote repair.
	const parseBomSheet = (buffer) => {
		let workbook
		try {
			workbook = XLSX.read(new Uint8Array(buffer), { type: "array" })
		} catch (error) {
			throw new Error("The BOM sheet could not be read from this file")
		}
		const worksheet = workbook.Sheets[workbook.SheetNames[BOM_SHEET_INDEX]]
		if (!worksheet) throw new Error("The BOM sheet could not be found in this file")
		return XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false })
	}
	const readBomSheet = (file) => new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onerror = () => reject(new Error("The file could not be read"))
		reader.onload = (loaded) => {
			try {
				resolve(parseBomSheet(loaded.target.result))
			} catch (error) {
				reject(error)
			}
		}
		reader.readAsArrayBuffer(file)
	})
	const generateHardwareBomRows = async (file) => {
		setBusyMessage("Generating the Hardware BOM from the IMOS list")
		const files = await HardwareBomService.generateHardwareBom(selectedProject._id, file)
		setHardwareBomFiles(files)
		return parseBomSheet(await HardwareBomService.downloadFile(files.bomUrl))
	}

	const uploadBom = async (event) => {
		const file = event.target.files[0]
		// Clearing the input lets the same file be picked again after a failed upload.
		event.target.value = null
		if (!file) return

		if (file.name.split('.').reverse()[0] !== 'xlsx') {
			setSnackInfo('Please upload the file in the correct format', "error")
			return
		}

		try {
			if (generatesHardwareBom) setHardwareBomFiles(null)
			else setBusyMessage("Reading the BOM file")
			// The material list is usually already warm, so this costs nothing.
			const [rows, knownMaterials] = await Promise.all([
				generatesHardwareBom ? generateHardwareBomRows(file) : readBomSheet(file),
				loadMaterialNames()
			])

			const usableRows = rows.filter(row => String(row['Select Room'] ?? "").trim() !== "")
			if (usableRows.length === 0) {
				setBusyMessage(null)
				setSnackInfo("This file has no BOM rows to upload", "error")
				return
			}

			const missingMaterials = new Set()
			usableRows.forEach(row => {
				const material = row['Select Material']
				if (material && !knownMaterials.has(material)) missingMaterials.add(material)
			})
			if (missingMaterials.size > 0) {
				setBusyMessage(null)
				setMissingMaterialMessage(`\n${Array.from(missingMaterials).join("\n")}`)
				setMissingMaterialOpen(true)
				return
			}

			const normalisedRows = usableRows.map((row) => ({
				...row,
				'Select Material': row['Select Material']
					?.toLowerCase()
					?.replace(/\b\w/g, (match) => match.toUpperCase())
			}))

			setBusyMessage(`Uploading ${normalisedRows.length} row${normalisedRows.length === 1 ? "" : "s"}`)
			const request = uploadEndpoint(selectedProject._id, { bomType: storedBomType, isMismatchedHardware, data: normalisedRows })
			if (!request) {
				setBusyMessage(null)
				setSnackInfo("This BOM type cannot be uploaded", "error")
				return
			}

			const result = await request
			if (result.status === 201) {
				setSnackInfo(`${bomType.label} upload success`, "success")
				// Hold the loader until the reloaded table has actually rendered.
				setBusyMessage("Loading the uploaded BOM")
				setTableReady(false)
				setRefreshKey(key => key + 1)
			} else {
				setBusyMessage(null)
			}
		} catch (error) {
			console.error(error)
			setBusyMessage(null)
			setSnackInfo(error?.response?.data ?? error?.message ?? "Error while uploading BOM", "error")
		}
	}

	const toggleSkip = async () => {
		const nextSkipped = !skipped
		setSavingSkip(true)
		try {
			const response = await HttpService.setPushToProdBomSkip({
				projectId: selectedProject._id,
				bomType: bomType.key,
				skipped: nextSkipped
			})
			setSkipped(nextSkipped)
			// Coming back from N/A remounts the table, so wait for it before dropping the loader.
			if (!nextSkipped) {
				setTableReady(false)
				setBusyMessage("Loading the BOM")
				setRefreshKey(key => key + 1)
			}
			setSnackInfo(response.data?.message ?? "BOM status updated", "success")
		} catch (error) {
			console.error(error)
			setSnackInfo(error?.response?.data?.message ?? "Could not update the BOM status", "error")
		}
		setSavingSkip(false)
	}

	const closeDialog = () => {
		closedRef.current = true
		onClose()
	}

	if (!project || !bomType) return null

	const chipStyle = STATE_STYLES[skipped ? "skipped" : (status?.state || "notUploaded")] || STATE_STYLES.notUploaded
	const chipLabel = skipped ? "N/A - Not for this project" : (status?.label || "Not Uploaded")
	const inputId = `push-to-prod-bom-upload-${bomType.key.replace(/\s+/g, "-")}`
	// Only ever wait on a BOM that exists. A column with nothing stored opens straight onto an
	// empty table, because waiting there would just be blocking the upload the user came to do.
	const uploading = Boolean(busyMessage)
	const awaitingStoredBom = !skipped && !tableReady && STORED_BOM_STATES.includes(status?.state)
	const busy = uploading || awaitingStoredBom
	const overlayMessage = busyMessage || "Loading the BOM"

	return (
		<>
			<Dialog
				open={open}
				onClose={closeDialog}
				TransitionProps={{
					onEntered: () => setContentMounted(true),
					onExited: props.onExited
				}}
				maxWidth="xl"
				fullWidth
				scroll="paper"
			>
				<DialogTitle disableTypography className={classes.title}>
					<div>
						<div className={classes.titleMain}>{bomType.label} - {project.projectNumber}</div>
						<div className={classes.titleSub}>
							{project.clientName ? `${project.clientName} · ` : ""}Upload, review and submit this BOM without leaving the page.
						</div>
						<Chip size="small" className={classes.statusChip} style={{ ...chipStyle, marginTop: 10 }} label={chipLabel} />
					</div>
					<IconButton size="small" className={classes.closeButton} onClick={closeDialog}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<div className={classes.progressSlot}>
					{busy && <LinearProgress className={classes.progressBar} />}
				</div>

				<DialogContent className={classes.content} dividers>
					<div className={classes.sectionLabel}>Upload New BOM</div>
					<div className={classes.actionRow}>
						<input
							accept="*"
							className={classes.hiddenInput}
							id={inputId}
							onChange={uploadBom}
							type="file"
							disabled={skipped || uploading}
						/>
						<label htmlFor={inputId}>
							<Button
								variant="contained"
								component="span"
								className={classes.uploadButton}
								startIcon={<PublishIcon />}
								disabled={skipped || uploading}
								style={(skipped || uploading) ? { background: "#D3D4D9" } : {}}
							>
								{generatesHardwareBom ? "Upload Hardware BOM" : `Upload ${bomType.label}`}
							</Button>
						</label>

						{hardwareBomFiles && (
							<>
								<Button variant="outlined" href={hardwareBomFiles.bomUrl} startIcon={<DownloadOutlined />}>BOM.xlsx</Button>
								<Button variant="outlined" href={hardwareBomFiles.reportUrl} startIcon={<DownloadOutlined />}>Report.xlsx</Button>
							</>
						)}

						<Tooltip title="Download sample BOM">
							<span>
								<CSVLink
									data={SAMPLE_DATA}
									headers={SAMPLE_HEADERS}
									filename="sample_bom.xlsx"
									className="csv-download-button"
									target="_blank"
								>
									<IconButton color="primary">
										<DownloadOutlined style={{ color: "#355025" }} />
									</IconButton>
								</CSVLink>
							</span>
						</Tooltip>

						<Tooltip title={skipped
							? "Make this BOM required for this project again"
							: "Skip this BOM for this project. Skipped BOMs do not block push to production."}>
							<span>
								<Button
									variant={skipped ? "contained" : "outlined"}
									className={skipped ? classes.skipButtonActive : classes.skipButton}
									startIcon={savingSkip
										? <CircularProgress size={16} style={{ color: "inherit" }} />
										: (skipped ? <ReplayIcon /> : <BlockIcon />)}
									disabled={savingSkip || uploading}
									onClick={toggleSkip}
								>
									{skipped ? "Mark as required" : "N/A Not for this project"}
								</Button>
							</span>
						</Tooltip>
					</div>

					<Divider style={{ marginBottom: 16 }} />

					{skipped
						? (
							<div className={classes.skippedPanel}>
								<div className={classes.skippedTitle}>{bomType.label} is not applicable for {project.projectNumber}</div>
								<div style={{ fontSize: 13 }}>
									This BOM is skipped, so it no longer blocks push to production.
									Choose "Mark as required" above to upload it after all.
								</div>
							</div>
						)
						: (
							<div className={classes.tableArea}>
								{busy && (
									<div className={classes.overlay}>
										<CircularProgress size={34} style={{ color: NAVY }} />
										<div className={classes.overlayMessage}>{overlayMessage}</div>
										<div className={classes.overlayHint}>The table appears as soon as every row is ready.</div>
										<div style={{ width: "100%", marginTop: 24, padding: "0 8px" }}>
											{[0, 1, 2, 3, 4].map(row => (
												<div key={row} className={classes.skeletonRow}>
													{[3, 3, 3, 4, 2, 1].map((flex, cell) => (
														<Skeleton key={cell} variant="rect" height={34} style={{ flex, borderRadius: 6 }} />
													))}
												</div>
											))}
										</div>
									</div>
								)}
								<div className={busy ? classes.tableBusy : ""}>
									{contentMounted && (
										<BomTable
											key={`${selectedProject._id}-${bomType.key}-${refreshKey}`}
											MOUNTED={refreshKey}
											SELECTEDPROJECT={selectedProject}
											TYPE={"CREATE"}
											BOMTYPE={storedBomType}
											VIEWBOMTYPE={storedBomType}
											ISMISMATCHEDHARDWARE={isMismatchedHardware}
											VIEWMISMATCHEDHARDWARE={isMismatchedHardware}
											BUTTONCLICKEDAPPROVED={true}
											SUPPRESSGLOBALLOADER={true}
											SKIPCOMMONBOMITEMS={true}
											INCLUDESAVED={true}
											ONRESET={() => setHardwareBomFiles(null)}
											ONREADY={onTableReady}
										/>
									)}
								</div>
							</div>
						)}
				</DialogContent>

				<DialogActions>
					<Button onClick={closeDialog} color="primary">Close</Button>
				</DialogActions>
			</Dialog>

			<AlertDialog
				open={missingMaterialOpen}
				onClose={() => setMissingMaterialOpen(false)}
				message={missingMaterialMessage}
			/>
		</>
	)
}

export default connect(null, { setSnackInfo })(BomUploadDialog)
